import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { graphqlClient } from './lib/graphql-client';
import type { DashboardStats, VerticalOverview } from 'dashboard-types';

const STATS_QUERY = `
  query GetDashboardStats {
    stats {
      totalProjects
      totalWorkers
      totalGraphQLAPIs
      totalD1Databases
      totalR2Buckets
      totalServiceBindings
      projectsByVertical {
        neureus
        finhub
        lazer
        foundation
      }
      projectsByStatus {
        active
        development
        maintenance
        archived
        planned
      }
    }
    verticals {
      vertical
      projectCount
      workerCount
      categories {
        category
        projectCount
      }
    }
  }
`;

interface DashboardData {
  stats: DashboardStats;
  verticals: VerticalOverview[];
}

function App() {
  const [activeVertical, setActiveVertical] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await graphqlClient.request<DashboardData>(STATS_QUERY);
      return response;
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="card max-w-md">
          <h2 className="text-red-600 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 dark:text-gray-400">{error.message}</p>
          <p className="text-sm text-gray-500 mt-4">Make sure the GraphQL API is running on http://localhost:8787</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-xs border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Monorepo Dashboard</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Managing 70+ projects across 4 business verticals
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">API: Connected</span>
              <div className="h-3 w-3 bg-green-500 rounded-full"></div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
          <div className="card">
            <div className="text-sm text-gray-500 dark:text-gray-400">Projects</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {data?.stats.totalProjects}
            </div>
          </div>

          <div className="card">
            <div className="text-sm text-gray-500 dark:text-gray-400">Workers</div>
            <div className="text-3xl font-bold text-blue-600 mt-2">{data?.stats.totalWorkers}</div>
          </div>

          <div className="card">
            <div className="text-sm text-gray-500 dark:text-gray-400">GraphQL APIs</div>
            <div className="text-3xl font-bold text-purple-600 mt-2">{data?.stats.totalGraphQLAPIs}</div>
          </div>

          <div className="card">
            <div className="text-sm text-gray-500 dark:text-gray-400">D1 Databases</div>
            <div className="text-3xl font-bold text-green-600 mt-2">{data?.stats.totalD1Databases}</div>
          </div>

          <div className="card">
            <div className="text-sm text-gray-500 dark:text-gray-400">R2 Buckets</div>
            <div className="text-3xl font-bold text-orange-600 mt-2">{data?.stats.totalR2Buckets}</div>
          </div>

          <div className="card">
            <div className="text-sm text-gray-500 dark:text-gray-400">Service Bindings</div>
            <div className="text-3xl font-bold text-red-600 mt-2">{data?.stats.totalServiceBindings}</div>
          </div>
        </div>

        {/* Verticals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {data?.verticals.map((vertical) => (
            <div
              key={vertical.vertical}
              className={`card cursor-pointer transition-all hover:shadow-lg ${
                activeVertical === vertical.vertical ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => setActiveVertical(activeVertical === vertical.vertical ? null : vertical.vertical)}
            >
              <div className="flex items-center justify-between mb-4">
                <span className={`badge badge-${vertical.vertical.toLowerCase()}`}>{vertical.vertical}</span>
                <span className="text-sm text-gray-500">{vertical.projectCount} projects</span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Workers</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{vertical.workerCount}</span>
                </div>

                {activeVertical === vertical.vertical && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Categories</h4>
                    {vertical.categories.map((cat) => (
                      <div key={cat.category} className="flex items-center justify-between text-sm py-1">
                        <span className="text-gray-600 dark:text-gray-400">{cat.category}</span>
                        <span className="text-gray-900 dark:text-white">{cat.projectCount}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Status Overview */}
        <div className="card">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Project Status Distribution</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {data?.stats.projectsByStatus && (
              <>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{data.stats.projectsByStatus.active}</div>
                  <div className="text-sm text-gray-500">Active</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{data.stats.projectsByStatus.development}</div>
                  <div className="text-sm text-gray-500">Development</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{data.stats.projectsByStatus.maintenance}</div>
                  <div className="text-sm text-gray-500">Maintenance</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-600">{data.stats.projectsByStatus.archived}</div>
                  <div className="text-sm text-gray-500">Archived</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{data.stats.projectsByStatus.planned}</div>
                  <div className="text-sm text-gray-500">Planned</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Placeholder for future features */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Coming Soon</h3>
            <ul className="space-y-2 text-gray-600 dark:text-gray-400">
              <li>• Project search and filtering</li>
              <li>• Worker health monitoring</li>
              <li>• Dependency graph visualization</li>
              <li>• Service binding explorer</li>
            </ul>
          </div>

          <div className="card">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button className="btn-primary w-full">Refresh Cache</button>
              <button className="btn-secondary w-full">Export Data</button>
              <button className="btn-secondary w-full">View Documentation</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
